import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Music, Plus, Trash2, Edit2, Upload, Link as LinkIcon, Youtube, Radio, AlertCircle, Play, Calendar, MapPin, Clock, DollarSign, Ticket, Heart, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';

const API = "/api";

const EMBED_TYPES = [
  { value: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://www.youtube.com/watch?v=...' },
  { value: 'soundcloud', label: 'SoundCloud', icon: Radio, placeholder: 'https://soundcloud.com/artist/track' },
  { value: 'spotify', label: 'Spotify', icon: Music, placeholder: 'https://open.spotify.com/track/...' },
];

const PRICE_TIERS = [
  { value: 'free', label: 'Free' },
  { value: '$', label: '$ (Under $20)' },
  { value: '$$', label: '$$ ($20-50)' },
  { value: '$$$', label: '$$$ ($50+)' },
];

const VIBES = [
  { value: 'casual', label: 'Casual' },
  { value: 'upbeat', label: 'Upbeat' },
  { value: 'chill', label: 'Chill' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'intimate', label: 'Intimate' },
  { value: 'wild', label: 'Wild' },
];

const MAX_TRACKS = 6;

const BandDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const { playTrack, addToPlaylist, refreshPlaylist } = usePlayer();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('music');
  const [tracks, setTracks] = useState([]);
  const [events, setEvents] = useState([]);
  const [artistProfile, setArtistProfile] = useState(null);
  const [tipJarForm, setTipJarForm] = useState({ venmo: '', cashapp: '', paypal: '', apple_pay: '' });
  const [tipJarSaving, setTipJarSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddTrackDialog, setShowAddTrackDialog] = useState(false);
  const [showAddEventDialog, setShowAddEventDialog] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [trackForm, setTrackForm] = useState({
    title: '',
    embed_type: 'youtube',
    embed_url: '',
    thumbnail: '',
    genre: ''
  });

  const [eventForm, setEventForm] = useState({
    title: '',
    venue: '',
    description: '',
    date: '',
    time: '',
    ticket_url: '',
    price_tier: 'free',
    genre: '',
    vibe: ''
  });

  const fetchMyTracks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/band/my-tracks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracks(response.data);
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
    }
  }, [token]);

  const fetchMyEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/band/my-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  }, [token]);

  const fetchMyProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/band/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setArtistProfile(response.data);
        const tj = response.data.tip_jar || {};
        setTipJarForm({
          venmo: tj.venmo || '',
          cashapp: tj.cashapp || '',
          paypal: tj.paypal || '',
          apple_pay: tj.apple_pay || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    Promise.all([fetchMyTracks(), fetchMyEvents(), fetchMyProfile()]).finally(() => setLoading(false));
  }, [user, authLoading, navigate, fetchMyTracks, fetchMyEvents, fetchMyProfile]);

  const resetTrackForm = () => {
    setTrackForm({ title: '', embed_type: 'youtube', embed_url: '', thumbnail: '', genre: '' });
    setEditingTrack(null);
  };

  const resetEventForm = () => {
    setEventForm({ title: '', venue: '', description: '', date: '', time: '', ticket_url: '', price_tier: 'free', genre: '', vibe: '' });
    setEditingEvent(null);
  };

  const convertToEmbedUrl = (url, type) => {
    if (type === 'youtube') {
      const videoId = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/)?.[1];
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const embedUrl = convertToEmbedUrl(trackForm.embed_url, trackForm.embed_type);
      const payload = { ...trackForm, embed_url: embedUrl };

      if (editingTrack) {
        await axios.put(`${API}/band/tracks/${editingTrack.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Track updated successfully!');
      } else {
        const response = await axios.post(`${API}/band/tracks`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Track added successfully!');
        if (response.data.track) addToPlaylist(response.data.track);
      }
      
      fetchMyTracks();
      refreshPlaylist();
      setShowAddTrackDialog(false);
      resetTrackForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save track');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingEvent) {
        await axios.put(`${API}/band/events/${editingEvent.id}`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event updated successfully!');
      } else {
        await axios.post(`${API}/band/events`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created! It will appear on the Events page.');
      }
      
      fetchMyEvents();
      setShowAddEventDialog(false);
      resetEventForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track?')) return;
    try {
      await axios.delete(`${API}/band/tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Track deleted');
      fetchMyTracks();
      refreshPlaylist();
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/band/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchMyEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleEditTrack = (track) => {
    setTrackForm({
      title: track.title,
      embed_type: track.embed_type,
      embed_url: track.embed_url,
      thumbnail: track.thumbnail || '',
      genre: track.genre || ''
    });
    setEditingTrack(track);
    setShowAddTrackDialog(true);
  };

  const handleEditEvent = (event) => {
    setEventForm({
      title: event.title,
      venue: event.venue,
      description: event.description || '',
      date: event.date,
      time: event.time,
      ticket_url: event.ticket_url || '',
      price_tier: event.price_tier || 'free',
      genre: event.genre || '',
      vibe: event.vibe || ''
    });
    setEditingEvent(event);
    setShowAddEventDialog(true);
  };

  const handlePlayTrack = (track) => {
    playTrack(track, { name: user?.name || 'Unknown Artist' });
  };

  const handleTipJarSave = async () => {
    setTipJarSaving(true);
    try {
      const response = await axios.put(`${API}/band/tip-jar`, tipJarForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tip jar updated!');
      setArtistProfile(prev => prev ? { ...prev, tip_jar: response.data.tip_jar } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tip jar');
    } finally {
      setTipJarSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-3xl font-bold">Band Dashboard</h1>
        <p className="text-muted-foreground">Manage your music and upcoming shows</p>
      </motion.div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="music" className="gap-2">
            <Music size={16} /> Music ({tracks.length}/{MAX_TRACKS})
          </TabsTrigger>
          <TabsTrigger value="events" className="gap-2">
            <Calendar size={16} /> Shows ({events.length})
          </TabsTrigger>
          <TabsTrigger value="tipjar" className="gap-2" data-testid="tipjar-tab">
            <DollarSign size={16} /> Tip Jar
          </TabsTrigger>
        </TabsList>

        {/* MUSIC TAB */}
        <TabsContent value="music" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{tracks.length} / {MAX_TRACKS} tracks</p>
            <Dialog open={showAddTrackDialog} onOpenChange={(open) => { setShowAddTrackDialog(open); if (!open) resetTrackForm(); }}>
              <DialogTrigger asChild>
                <Button disabled={tracks.length >= MAX_TRACKS} className="gap-2">
                  <Plus size={18} /> Add Track
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingTrack ? 'Edit Track' : 'Add New Track'}</DialogTitle>
                  <DialogDescription>Share your music via YouTube, SoundCloud, or Spotify</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleTrackSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Track Title *</Label>
                    <Input value={trackForm.title} onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })} placeholder="Enter track title" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Platform *</Label>
                    <Select value={trackForm.embed_type} onValueChange={(value) => setTrackForm({ ...trackForm, embed_type: value })}>
                      <SelectTrigger><SelectValue placeholder="Select platform" /></SelectTrigger>
                      <SelectContent>
                        {EMBED_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2"><type.icon size={14} />{type.label}</div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Track URL *</Label>
                    <Input value={trackForm.embed_url} onChange={(e) => setTrackForm({ ...trackForm, embed_url: e.target.value })} placeholder={EMBED_TYPES.find(t => t.value === trackForm.embed_type)?.placeholder} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Genre (Optional)</Label>
                    <Input value={trackForm.genre} onChange={(e) => setTrackForm({ ...trackForm, genre: e.target.value })} placeholder="e.g., Hip Hop, Rock, Indie" />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowAddTrackDialog(false)}>Cancel</Button>
                    <Button type="submit" disabled={submitting}>{submitting ? 'Saving...' : (editingTrack ? 'Update' : 'Add Track')}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {tracks.length >= MAX_TRACKS && (
            <div className="flex items-center gap-2 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
              <AlertCircle className="text-yellow-500" size={20} />
              <p className="text-sm">Maximum {MAX_TRACKS} tracks reached. Delete a track to add a new one.</p>
            </div>
          )}

          {tracks.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Music size={48} className="text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">No tracks yet</h3>
                <p className="text-muted-foreground text-center mb-4">Add your first track to start building your profile.</p>
                <Button onClick={() => setShowAddTrackDialog(true)} className="gap-2"><Plus size={18} />Add Your First Track</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tracks.map((track) => (
                <Card key={track.id} className="overflow-hidden group">
                  <div className="relative aspect-video">
                    <img src={track.thumbnail || 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=400'} alt={track.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="icon" variant="secondary" onClick={() => handlePlayTrack(track)}><Play size={18} /></Button>
                      <Button size="icon" variant="secondary" onClick={() => handleEditTrack(track)}><Edit2 size={18} /></Button>
                      <Button size="icon" variant="destructive" onClick={() => handleDeleteTrack(track.id)}><Trash2 size={18} /></Button>
                    </div>
                    <div className="absolute top-2 right-2 bg-black/60 px-2 py-1 rounded text-xs flex items-center gap-1">
                      {track.embed_type === 'youtube' && <Youtube size={12} />}
                      {track.embed_type === 'soundcloud' && <Radio size={12} />}
                      {track.embed_type === 'spotify' && <Music size={12} />}
                      {track.embed_type}
                    </div>
                  </div>
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg truncate">{track.title}</CardTitle>
                    <CardDescription className="flex items-center justify-between">
                      <span>{track.genre || 'No genre'}</span>
                      <span className="text-xs">{track.play_count || 0} plays</span>
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* EVENTS TAB */}
        <TabsContent value="events" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{events.length} upcoming shows</p>
            <Dialog open={showAddEventDialog} onOpenChange={(open) => { setShowAddEventDialog(open); if (!open) resetEventForm(); }}>
              <DialogTrigger asChild>
                <Button className="gap-2"><Plus size={18} /> Add Show</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingEvent ? 'Edit Show' : 'Add Upcoming Show'}</DialogTitle>
                  <DialogDescription>Add your show to the Twin Tube events calendar</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleEventSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Event Title *</Label>
                    <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="e.g., Live at First Avenue" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Venue *</Label>
                    <Input value={eventForm.venue} onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })} placeholder="e.g., First Avenue, Minneapolis" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Time *</Label>
                      <Input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Tell fans what to expect..." rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price Range</Label>
                      <Select value={eventForm.price_tier} onValueChange={(value) => setEventForm({ ...eventForm, price_tier: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PRICE_TIERS.map((tier) => (
                            <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Vibe</Label>
                      <Select value={eventForm.vibe} onValueChange={(value) => setEventForm({ ...eventForm, vibe: value })}>
                        <SelectTrigger><SelectValue placeholder="Select vibe" /></SelectTrigger>
                        <SelectContent>
                          {VIBES.map((vibe) => (
                            <SelectItem key={vibe.value} value={vibe.value}>{vibe.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Genre</Label>
                    <Input value={eventForm.genre} onChange={(e) => setEventForm({ ...eventForm, genre: e.target.value })} placeholder="e.g., Hip Hop, Rock, Jazz" />
                  </div>
                  <div className="space-y-2">
                    <Label>Ticket URL</Label>
                    <Input value={eventForm.ticket_url} onChange={(e) => setEventForm({ ...eventForm, ticket_url: e.target.value })} placeholder="https://tickets.example.com/..." />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowAddEventDialog(false)}>Cancel</Button>
                    <Button type="submit" disabled={submitting}>{submitting ? 'Saving...' : (editingEvent ? 'Update' : 'Add Show')}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {events.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar size={48} className="text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">No shows scheduled</h3>
                <p className="text-muted-foreground text-center mb-4">Add your upcoming shows to let fans know where to see you.</p>
                <Button onClick={() => setShowAddEventDialog(true)} className="gap-2"><Plus size={18} />Add Your First Show</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <Card key={event.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-bold">{event.title}</h3>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><MapPin size={14} />{event.venue}</span>
                          <span className="flex items-center gap-1"><Calendar size={14} />{new Date(event.date).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Clock size={14} />{event.time}</span>
                          {event.price_tier && <span className="flex items-center gap-1"><DollarSign size={14} />{event.price_tier}</span>}
                        </div>
                        {event.description && <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{event.description}</p>}
                        <div className="flex items-center gap-2 mt-3">
                          {event.genre && <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">{event.genre}</span>}
                          {event.vibe && <span className="text-xs bg-secondary/20 text-secondary px-2 py-1 rounded">{event.vibe}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {event.ticket_url && (
                          <Button size="sm" variant="outline" onClick={() => window.open(event.ticket_url, '_blank')} className="gap-1">
                            <Ticket size={14} /> Tickets
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => handleEditEvent(event)}><Edit2 size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteEvent(event.id)} className="text-destructive"><Trash2 size={16} /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TIP JAR TAB */}
        <TabsContent value="tipjar" className="space-y-6 mt-6">
          {!artistProfile ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <DollarSign size={48} className="text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">No Artist Profile Found</h3>
                <p className="text-muted-foreground text-center mb-4">Your band registration needs to be approved before you can set up a tip jar.</p>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="tipjar-settings">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="text-green-400" size={20} /> Tip Jar Settings
                </CardTitle>
                <CardDescription>
                  Let fans support you directly. Add your payment links and they'll appear on your artist profile.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-[#008CFF] flex items-center justify-center text-white text-xs font-bold">V</div>
                    Venmo
                  </Label>
                  <Input
                    value={tipJarForm.venmo}
                    onChange={(e) => setTipJarForm({ ...tipJarForm, venmo: e.target.value })}
                    placeholder="@your-venmo-username"
                    data-testid="tipjar-venmo-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-[#00D632] flex items-center justify-center text-white text-xs font-bold">$</div>
                    Cash App
                  </Label>
                  <Input
                    value={tipJarForm.cashapp}
                    onChange={(e) => setTipJarForm({ ...tipJarForm, cashapp: e.target.value })}
                    placeholder="$yourcashtag"
                    data-testid="tipjar-cashapp-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-[#0070BA] flex items-center justify-center text-white text-xs font-bold">P</div>
                    PayPal
                  </Label>
                  <Input
                    value={tipJarForm.paypal}
                    onChange={(e) => setTipJarForm({ ...tipJarForm, paypal: e.target.value })}
                    placeholder="your-paypal-username"
                    data-testid="tipjar-paypal-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-gray-600 flex items-center justify-center text-white text-xs font-bold">A</div>
                    Apple Pay
                  </Label>
                  <Input
                    value={tipJarForm.apple_pay}
                    onChange={(e) => setTipJarForm({ ...tipJarForm, apple_pay: e.target.value })}
                    placeholder="Phone number or email"
                    data-testid="tipjar-applepay-input"
                  />
                </div>

                <Button 
                  onClick={handleTipJarSave} 
                  disabled={tipJarSaving} 
                  className="w-full gap-2"
                  data-testid="tipjar-save-btn"
                >
                  <Save size={16} />
                  {tipJarSaving ? 'Saving...' : 'Save Tip Jar'}
                </Button>

                {artistProfile.tip_jar && Object.keys(artistProfile.tip_jar).length > 0 && (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-400 font-medium mb-2">Active Payment Links:</p>
                    <div className="flex flex-wrap gap-2">
                      {artistProfile.tip_jar.venmo && <span className="text-xs bg-[#008CFF]/20 text-[#008CFF] px-2 py-1 rounded">Venmo</span>}
                      {artistProfile.tip_jar.cashapp && <span className="text-xs bg-[#00D632]/20 text-[#00D632] px-2 py-1 rounded">Cash App</span>}
                      {artistProfile.tip_jar.paypal && <span className="text-xs bg-[#0070BA]/20 text-[#0070BA] px-2 py-1 rounded">PayPal</span>}
                      {artistProfile.tip_jar.apple_pay && <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded">Apple Pay</span>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {activeTab === 'music' ? <Music className="text-primary" size={20} /> : <Calendar className="text-primary" size={20} />}
            {activeTab === 'music' ? 'Music Tips' : 'Event Tips'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          {activeTab === 'music' ? (
            <>
              <p>• Add up to {MAX_TRACKS} tracks to your profile</p>
              <p>• Your tracks appear in the auto-play playlist</p>
              <p>• YouTube links work best for playback</p>
            </>
          ) : (
            <>
              <p>• Your shows appear on the main Events page</p>
              <p>• Add ticket links so fans can buy tickets</p>
              <p>• Include price and vibe info for better discovery</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BandDashboard;
